import { UserDTO } from "@/generated/api/models";
import { InterfaceModalProps } from "@gluestack-ui/modal/lib/types";
import { useEffect, useMemo } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/utils/useToast";
import {
  getUserControllerMeQueryKey,
  useUserBeneficiaryControllerUpdateBeneficiary,
} from "@/generated/api/api";
import {
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  SafeAreaView,
} from "react-native";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { Heading } from "@/components/ui/heading";
import { ChevronDownIcon, CloseIcon, Icon } from "@/components/ui/icon";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlHelper,
  FormControlHelperText,
  FormControlLabel,
  FormControlLabelText,
} from "@/components/ui/form-control";
import { Input, InputField } from "@/components/ui/input";
import { AlertTriangle } from "lucide-react-native";
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectScrollView,
  SelectTrigger,
} from "@/components/ui/select";
import {
  countryListAlpha2,
  countryListAlpha2EU,
  currencyList,
} from "./country.list";
import { Button, ButtonSpinner, ButtonText } from "@/components/ui/button";
import { useMobileBreakpoint } from "@/utils/useMobileBreakpoint";
import { extractIBAN, isSEPACountry } from "ibantools";
import { is40x } from "@/utils/error.checker";
import { FetchError } from "@/utils/api.fetch.client";

const ProfileBeneficiarySchema = z.object({
  currency: z
    .string()
    .min(1, "Required")
    .refine(
      (val) => val === "EUR",
      "Payouts restricted to only EUR at the moment. Please contact support"
    ),
  firstName: z.string().trim().min(1, "Required"),
  lastName: z.string().trim().min(1, "Required"),
  iban: z
    .string()
    .min(1, "Required")
    .superRefine((val, ctx) => {
      const ibanSpec = extractIBAN(val);

      if (!ibanSpec.valid || !ibanSpec.countryCode) {
        ctx.addIssue({
          code: "custom",
          message: "Please check IBAN validity",
        });

        return z.NEVER;
      }

      if (!isSEPACountry(ibanSpec.countryCode)) {
        ctx.addIssue({
          code: "custom",
          message:
            "This IBAN does not belong to SEPA country. Please contact support",
        });
      }
    }),
  streetLine1: z.string().min(1, "Required"),
  streetLine2: z.string().optional(),
  city: z.string().optional(),
  country: z
    .string()
    .min(1, "Required")
    .refine(
      (val) => Object.keys(countryListAlpha2).includes(val),
      "Invalid country. Please contact support"
    )
    .refine(
      (val) => Object.keys(countryListAlpha2EU).includes(val),
      "Service restricted to EU residents. Receiving and withdrawing tips is only supported inside the European Union. Additional address verification may be required."
    ),
  postCode: z.string().min(1, "Required"),
});

type ProfileBeneficiarySchemaType = z.infer<typeof ProfileBeneficiarySchema>;

export function ProfileBeneficiaryModalEdit(
  props: { user: UserDTO } & Pick<InterfaceModalProps, "isOpen" | "onClose">
) {
  const beneficiary = props.user.beneficiary;

  const defaultValues = useMemo(
    () => ({
      currency: beneficiary?.currency,
      firstName: beneficiary?.firstName,
      lastName: beneficiary?.lastName,
      iban: beneficiary?.iban,
      streetLine1: beneficiary?.streetLine1,
      streetLine2: beneficiary?.streetLine2 ?? undefined,
      city: beneficiary?.city ?? undefined,
      country: beneficiary?.country,
      postCode: beneficiary?.postCode,
    }),
    [
      beneficiary?.currency,
      beneficiary?.firstName,
      beneficiary?.lastName,
      beneficiary?.iban,
      beneficiary?.streetLine1,
      beneficiary?.streetLine2,
      beneficiary?.city,
      beneficiary?.country,
      beneficiary?.postCode,
    ]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ProfileBeneficiarySchemaType>({
    resolver: zodResolver(ProfileBeneficiarySchema),
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [props.user]);

  const queryClient = useQueryClient();
  const { showToast, showError } = useToast();

  const { mutateAsync } = useUserBeneficiaryControllerUpdateBeneficiary({
    mutation: {
      meta: {
        noSystemError: true,
      },
      onError(error) {
        if (error instanceof FetchError && error.payload.status === 409) {
          showError("This IBAN is already registered. Please contact support.");
        } else {
          showError(error);
        }
      },
      onSuccess: (data) => {
        showToast("success", "Banking details has been successfully updated");
        queryClient.setQueryData(
          getUserControllerMeQueryKey(),
          (old: UserDTO | undefined) => {
            if (!old) {
              return;
            }

            return {
              ...old,
              beneficiary: data,
            };
          }
        );
        reset({
          currency: data?.currency,
          firstName: data?.firstName,
          lastName: data?.lastName,
          iban: data?.iban,
          streetLine1: data?.streetLine1,
          streetLine2: data?.streetLine2 ?? undefined,
          city: data?.city ?? undefined,
          country: data?.country,
          postCode: data?.postCode,
        });
        props.onClose?.();
      },
    },
  });

  const ibanValue = useWatch({ control, name: "iban" });

  const onSubmit = (data: ProfileBeneficiarySchemaType) => {
    return mutateAsync({
      data: {
        ...props.user.beneficiary,
        ...data,
        bankCountry: extractIBAN(ibanValue).countryCode ?? "",
      },
    }).catch(() => {});
  };

  const handleKeyPress =
    (submit = false) =>
    () => {
      Keyboard.dismiss();
      if (submit) {
        handleSubmit(onSubmit)();
      }
    };

  const formControls = (
    <>
      <FormControl isRequired isInvalid={!!errors?.iban} className="w-full">
        <FormControlLabel>
          <FormControlLabelText>IBAN</FormControlLabelText>
        </FormControlLabel>
        <Controller
          defaultValue=""
          name="iban"
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <Input>
              <InputField
                placeholder="Your IBAN"
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                onSubmitEditing={handleKeyPress()}
                returnKeyType="done"
              />
            </Input>
          )}
        />
        <FormControlError>
          <FormControlErrorIcon as={AlertTriangle} />
          <FormControlErrorText>{errors?.iban?.message}</FormControlErrorText>
        </FormControlError>
      </FormControl>
      <FormControl isRequired isInvalid={!!errors?.currency} className="w-full">
        <FormControlLabel>
          <FormControlLabelText>Currency</FormControlLabelText>
        </FormControlLabel>
        <Controller
          defaultValue={beneficiary?.currency}
          name="currency"
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <Select onValueChange={onChange} selectedValue={value}>
              <SelectTrigger>
                <SelectInput
                  placeholder="Select currency"
                  className="flex-1"
                  onBlur={onBlur}
                />
                <SelectIcon className="mr-3" as={ChevronDownIcon} />
              </SelectTrigger>
              <SelectPortal>
                <SelectBackdrop />
                <SelectContent>
                  <SelectDragIndicatorWrapper>
                    <SelectDragIndicator />
                  </SelectDragIndicatorWrapper>
                  {currencyList.map((c) => (
                    <SelectItem key={c} label={c} value={c} />
                  ))}
                </SelectContent>
              </SelectPortal>
            </Select>
          )}
        />
        <FormControlError>
          <FormControlErrorIcon as={AlertTriangle} />
          <FormControlErrorText>
            {errors?.currency?.message}
          </FormControlErrorText>
        </FormControlError>
      </FormControl>
      <FormControl
        isInvalid={!!errors?.firstName}
        className="w-full"
        isRequired
      >
        <FormControlLabel>
          <FormControlLabelText>First name</FormControlLabelText>
        </FormControlLabel>
        <Controller
          defaultValue=""
          name="firstName"
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <Input>
              <InputField
                placeholder="First name of bank account"
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                onSubmitEditing={handleKeyPress()}
                returnKeyType="done"
              />
            </Input>
          )}
        />
        <FormControlError>
          <FormControlErrorIcon as={AlertTriangle} />
          <FormControlErrorText>
            {errors?.firstName?.message}
          </FormControlErrorText>
        </FormControlError>
      </FormControl>
      <FormControl isRequired isInvalid={!!errors?.lastName} className="w-full">
        <FormControlLabel>
          <FormControlLabelText>Last name</FormControlLabelText>
        </FormControlLabel>
        <Controller
          defaultValue=""
          name="lastName"
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <Input>
              <InputField
                placeholder="Last name of bank account"
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                onSubmitEditing={handleKeyPress()}
                returnKeyType="done"
              />
            </Input>
          )}
        />
        <FormControlError>
          <FormControlErrorIcon as={AlertTriangle} />
          <FormControlErrorText>
            {errors?.lastName?.message}
          </FormControlErrorText>
        </FormControlError>
      </FormControl>
      <FormControl
        isRequired
        isInvalid={!!errors?.streetLine1}
        className="w-full"
      >
        <FormControlLabel>
          <FormControlLabelText>Street line 1</FormControlLabelText>
        </FormControlLabel>
        <Controller
          defaultValue=""
          name="streetLine1"
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <Input>
              <InputField
                placeholder="Street address"
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                onSubmitEditing={handleKeyPress()}
                returnKeyType="done"
              />
            </Input>
          )}
        />
        <FormControlError>
          <FormControlErrorIcon as={AlertTriangle} />
          <FormControlErrorText>
            {errors?.streetLine1?.message}
          </FormControlErrorText>
        </FormControlError>
      </FormControl>
      <FormControl isInvalid={!!errors?.streetLine2} className="w-full">
        <FormControlLabel>
          <FormControlLabelText>Street line 2</FormControlLabelText>
        </FormControlLabel>
        <Controller
          defaultValue=""
          name="streetLine2"
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <Input>
              <InputField
                placeholder="Apartment, suite or space number (if applicable)"
                value={value ?? undefined}
                onChangeText={onChange}
                onBlur={onBlur}
                onSubmitEditing={handleKeyPress()}
                returnKeyType="done"
              />
            </Input>
          )}
        />
        <FormControlError>
          <FormControlErrorIcon as={AlertTriangle} />
          <FormControlErrorText>
            {errors?.streetLine2?.message}
          </FormControlErrorText>
        </FormControlError>
      </FormControl>
      <FormControl isRequired isInvalid={!!errors?.city} className="w-full">
        <FormControlLabel>
          <FormControlLabelText>City</FormControlLabelText>
        </FormControlLabel>
        <Controller
          defaultValue=""
          name="city"
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <Input>
              <InputField
                placeholder="City"
                value={value ?? undefined}
                onChangeText={onChange}
                onBlur={onBlur}
                onSubmitEditing={handleKeyPress()}
                returnKeyType="done"
              />
            </Input>
          )}
        />
        <FormControlError>
          <FormControlErrorIcon as={AlertTriangle} />
          <FormControlErrorText>{errors?.city?.message}</FormControlErrorText>
        </FormControlError>
      </FormControl>
      <FormControl isRequired isInvalid={!!errors?.postCode} className="w-full">
        <FormControlLabel>
          <FormControlLabelText>Postal code</FormControlLabelText>
        </FormControlLabel>
        <Controller
          defaultValue=""
          name="postCode"
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <Input>
              <InputField
                placeholder="Postal code"
                value={value ?? undefined}
                onChangeText={onChange}
                onBlur={onBlur}
                onSubmitEditing={handleKeyPress()}
                returnKeyType="done"
              />
            </Input>
          )}
        />
        <FormControlError>
          <FormControlErrorIcon as={AlertTriangle} />
          <FormControlErrorText>
            {errors?.postCode?.message}
          </FormControlErrorText>
        </FormControlError>
      </FormControl>
      <FormControl isRequired isInvalid={!!errors?.country} className="w-full">
        <FormControlLabel>
          <FormControlLabelText>Country</FormControlLabelText>
        </FormControlLabel>
        <Controller
          defaultValue=""
          name="country"
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <Select onValueChange={onChange} selectedValue={value}>
              <SelectTrigger>
                <SelectInput
                  placeholder="Select country"
                  className="flex-1"
                  onBlur={onBlur}
                  value={
                    countryListAlpha2[value as keyof typeof countryListAlpha2]
                  }
                />
                <SelectIcon className="mr-3" as={ChevronDownIcon} />
              </SelectTrigger>
              <SelectPortal snapPoints={[80]}>
                <SelectBackdrop />
                <SelectContent>
                  <SelectDragIndicatorWrapper>
                    <SelectDragIndicator />
                  </SelectDragIndicatorWrapper>
                  <SelectScrollView>
                    {Object.entries(countryListAlpha2).map(([code, name]) => (
                      <SelectItem key={code} label={name} value={code} />
                    ))}
                  </SelectScrollView>
                </SelectContent>
              </SelectPortal>
            </Select>
          )}
        />
        <FormControlError>
          <FormControlErrorIcon as={AlertTriangle} className="min-w-5" />
          <FormControlErrorText>
            {errors?.country?.message}
          </FormControlErrorText>
        </FormControlError>
      </FormControl>
    </>
  );

  const modalBody = (
    <ModalBody className="mb-4" contentContainerClassName="gap-4">
      {formControls}
    </ModalBody>
  );

  const bp = useMobileBreakpoint();

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      size={bp === "desktop" ? "md" : "full"}
    >
      <ModalBackdrop />
      <ModalContent className="max-h-screen-safe max-h-full">
        <ModalHeader>
          <Heading>Update banking details</Heading>
          <ModalCloseButton>
            <Icon
              as={CloseIcon}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
            />
          </ModalCloseButton>
        </ModalHeader>
        {Platform.OS === "web" ? (
          modalBody
        ) : (
          <KeyboardAvoidingView behavior="padding" className="flex-auto">
            {modalBody}
          </KeyboardAvoidingView>
        )}
        <ModalFooter className="flex-col items-start min-h-fit grow shrink-0">
          <Button
            className="w-full gap-2"
            onPress={handleSubmit(onSubmit)}
            isDisabled={isSubmitting}
          >
            <ButtonText>Save</ButtonText>
            {isSubmitting && <ButtonSpinner />}
          </Button>
        </ModalFooter>
      </ModalContent>
      {/* </SafeAreaView> */}
    </Modal>
  );
}
